import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './useAuth'
import { getWalletBalance } from '../api/walletApi'

/**
 * The signed-in customer's spendable store credit, for the nav chip.
 *
 * The chip rides in the global nav, so this runs on EVERY route — the landing
 * page included. Two things keep that from being a tax:
 *
 *  1. Signed-out visitors never fetch. Anonymous landing traffic (which is most
 *     landing traffic, and all of the Lighthouse-measured kind) is untouched,
 *     which matters on the one page already carrying a 3D scene.
 *  2. A signed-in customer fetches at most once per TTL, not once per
 *     navigation. Shop → product → shop → landing is one request, not four.
 *
 * That second rule is not just politeness. The gateway's request log
 * deliberately drops /api/auth/refresh because a call that "fires every few
 * minutes for every signed-in user and says nothing about what anyone was
 * doing" would swamp every other endpoint in the table. A per-navigation
 * balance poll has exactly that shape, and /api/wallet IS logged — so caching
 * here is what keeps it a meaningful row in the analytics rather than noise
 * that buries the rest.
 *
 * Returns 0 for a signed-out visitor and for any failure; the caller renders a
 * chip, and a chip is not worth an error state.
 */

// A balance only changes when the customer spends it (checkout tells us — see
// invalidateWalletBalance) or when an admin grants more, which happens
// out-of-band between sessions. A minute is short enough that a grant shows up
// while they browse, and long enough that browsing costs one request.
const TTL_MS = 60_000

let cache: { userId: string; balance: number; at: number } | null = null

/**
 * Drop the cached balance so the next render re-reads it.
 *
 * Called by checkout the moment an order settles: the credit is spent, and a
 * chip still advertising it for another minute would be the app lying about
 * money. This is the reason the TTL can be as long as it is.
 */
export function invalidateWalletBalance(): void {
  cache = null
}

export function useWalletBalance(): number {
  const { user, getToken } = useAuth()
  const location = useLocation()
  const [balance, setBalance] = useState(
    () => (cache && user && cache.userId === user.id ? cache.balance : 0)
  )

  useEffect(() => {
    if (!user) {
      // Signing out must clear both, or the next person on this browser would
      // briefly see the previous customer's balance.
      cache = null
      setBalance(0)
      return
    }

    const fresh =
      cache !== null && cache.userId === user.id && Date.now() - cache.at < TTL_MS

    if (fresh) {
      setBalance(cache!.balance)
      return
    }

    let alive = true
    ;(async () => {
      const token = await getToken()
      if (!token || !alive) return
      const wallet = await getWalletBalance(token)
      if (!alive) return
      const value = wallet?.balance ?? 0
      // Only cache a real answer. A failed read must not pin 0 for a minute and
      // hide a balance the customer actually has.
      if (wallet) cache = { userId: user.id, balance: value, at: Date.now() }
      setBalance(value)
    })()
    return () => { alive = false }
  }, [user, getToken, location.pathname])

  return balance
}
